import React, { ReactNode } from 'react';
import { ResizableHandle } from './ResizableHandle';
import { usePanelLayout, DefaultWidths } from '../../hooks/usePanelLayout';
import { usePanelResize } from '../../hooks/usePanelResize';

// 提取重复的SVG图标为常量
const CHEVRON_RIGHT_ICON = (
  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const CHEVRON_LEFT_ICON = (
  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

interface FourPanelLayoutProps {
  sidebar: ReactNode;
  leftPanel: ReactNode;
  centerPanel: ReactNode;
  rightPanel: ReactNode;
  /** 顶部栏组件（可选） */
  topBar?: ReactNode;
  defaultWidths?: DefaultWidths;
  onResize?: (panel: string, width: number) => void;
  sidebarWidth?: number;
  /** 中间面板是否折叠 */
  centerPanelCollapsed?: boolean;
}

/**
 * 四栏布局容器组件 - 参考 makeverse.cn 设计
 * 工作区核心布局：顶部栏 | 导航菜单 | 设定/章节列表 | 设定详情/正文 | 右侧面板
 */
export const FourPanelLayout: React.FC<FourPanelLayoutProps> = ({
  sidebar,
  leftPanel,
  centerPanel,
  rightPanel,
  topBar,
  defaultWidths = { leftPanel: 280, centerPanel: 'auto', rightPanel: 400 },
  onResize,
  sidebarWidth = 64,
  centerPanelCollapsed = false,
}) => {
  // 使用自定义Hook管理面板布局状态
  const {
    state: { leftPanelWidth, rightPanelWidth, leftPanelCollapsed, rightPanelCollapsed },
    updateLeftPanelWidth,
    updateRightPanelWidth,
    toggleLeftPanel,
    toggleRightPanel,
  } = usePanelLayout(defaultWidths);

  // 使用自定义Hook处理拖拽逻辑
  const { handleLeftResize, handleRightResize, setStartWidths, cleanup } = usePanelResize(
    sidebarWidth,
    (direction, width) => {
      if (direction === 'left') {
        updateLeftPanelWidth(width);
        onResize?.('leftPanel', width);
      } else {
        updateRightPanelWidth(width);
        onResize?.('rightPanel', width);
      }
    }
  );

  // 组件卸载时清理
  React.useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // 处理左侧分栏条拖动开始
  const handleLeftResizeStart = () => {
    setStartWidths(leftPanelWidth, rightPanelWidth);
  };

  // 处理右侧分栏条拖动开始
  const handleRightResizeStart = () => {
    setStartWidths(leftPanelWidth, rightPanelWidth);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
      {/* 顶部栏（可选） */}
      {topBar && (
        <div className="flex-shrink-0 bg-white border-b border-gray-100 shadow-sm">
          {topBar}
        </div>
      )}

      {/* 主体区域 */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* 左侧边栏 - 固定宽度 */}
        <div
          className="bg-white border-r border-gray-100 flex-shrink-0"
          style={{ width: `${sidebarWidth}px` }}
        >
          {sidebar}
        </div>

        {/* 左侧列表 - 可调整宽度，支持折叠 */}
        {!leftPanelCollapsed && (
          <div
            className="flex bg-white border-r border-gray-100 transition-all duration-150 ease-in-out"
            style={{ width: `${leftPanelWidth}px` }}
          >
            <div className="flex-1 overflow-hidden">
              {leftPanel}
            </div>
            {/* 可拖拽分隔线 */}
            <ResizableHandle onResize={handleLeftResize} onResizeStart={handleLeftResizeStart} direction="horizontal" />
          </div>
        )}

        {/* 左侧面板折叠按钮 */}
        {leftPanelCollapsed && (
          <button
            onClick={toggleLeftPanel}
            className="w-8 bg-gray-50 hover:bg-gray-100 border-r border-gray-100 flex items-center justify-center transition-colors"
            aria-label="展开左侧面板"
            aria-expanded={false}
          >
            {CHEVRON_RIGHT_ICON}
          </button>
        )}

        {/* 中间详情 - 自动宽度 */}
        {!centerPanelCollapsed && (
          <div className="flex-1 overflow-hidden bg-white">
            {centerPanel}
          </div>
        )}

        {/* 右侧面板 - 固定宽度，支持折叠，中间栏折叠时自动扩展 */}
        {!rightPanelCollapsed && (
          <div
            className={`flex bg-white border-l border-gray-100 transition-all duration-150 ease-in-out ${centerPanelCollapsed ? 'flex-1' : ''}`}
            style={centerPanelCollapsed ? undefined : { width: `${rightPanelWidth}px` }}
          >
            {!centerPanelCollapsed && (
              <ResizableHandle onResize={handleRightResize} onResizeStart={handleRightResizeStart} direction="horizontal" />
            )}
            <div className="flex-1 overflow-hidden">
              {rightPanel}
            </div>
          </div>
        )}

        {/* 右侧面板折叠按钮 */}
        {rightPanelCollapsed && (
          <button
            onClick={toggleRightPanel}
            className="w-8 bg-gray-50 hover:bg-gray-100 border-l border-gray-100 flex items-center justify-center transition-colors"
            aria-label="展开右侧面板"
            aria-expanded={false}
          >
            {CHEVRON_LEFT_ICON}
          </button>
        )}


      </div>
    </div>
  );
};

export default FourPanelLayout;
